import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmt } from "@/lib/dates";
import { SHIFT, PRIORITY, TASK_STATUS, HOLIDAY, ROSTER_STATUS, can } from "@/lib/colors";
import { Pill } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-800 text-right font-bold">{children}</span>
  </div>
);

const TaskDetails = ({ task, onChanged }) => {
  const { user } = useAuth();
  const canChange = task.assignee_id === user.id || task.assigned_by_id === user.id || can(user, "assign_tasks");
  const setStatus = async (status) => {
    try {
      await api.patch(`/tasks/${task.id}`, { status });
      toast.success("Task updated");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <>
      {task.description && <p className="text-sm text-slate-600 mb-3">{task.description}</p>}
      <Row label="Assigned to">{task.assignee_name}</Row>
      <Row label="Assigned by">{task.assigned_by_name}</Row>
      {task.project_name && <Row label="Project">{task.project_name}</Row>}
      <Row label="Due">{fmt(task.due, "EEE d MMM, HH:mm")}</Row>
      <Row label="Priority"><Pill cls={PRIORITY[task.priority].cls}>{PRIORITY[task.priority].label}</Pill></Row>
      <Row label="Status">
        {canChange ? (
          <Select value={task.status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 h-8" data-testid="event-task-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TASK_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : <Pill cls={TASK_STATUS[task.status].cls}>{TASK_STATUS[task.status].label}</Pill>}
      </Row>
    </>
  );
};

export const EventDialog = ({ selected, names, onClose, onChanged }) => {
  if (!selected) return null;
  const { kind, item } = selected;
  const title = kind === "task" ? item.title : kind === "shift" ? `${SHIFT[item.location].label} shift` : HOLIDAY[item.type].label;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="event-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#24474A]">{title}</DialogTitle>
          <DialogDescription>{kind === "task" ? "Task details" : kind === "shift" ? "Scheduled shift" : "Approved time off"}</DialogDescription>
        </DialogHeader>
        {kind === "task" && <TaskDetails task={item} onChanged={() => { onChanged(); onClose(); }} />}
        {kind === "shift" && (
          <>
            <Row label="Team member">{names[item.user_id] || "—"}</Row>
            <Row label="Date">{fmt(item.date, "EEEE d MMMM")}</Row>
            <Row label="Time">{item.start} – {item.end}</Row>
            <Row label="Location">{SHIFT[item.location].label}</Row>
            <Row label="Schedule status"><Pill cls={ROSTER_STATUS[item.roster_status].cls}>{ROSTER_STATUS[item.roster_status].label}</Pill></Row>
            {item.notes && <Row label="Notes">{item.notes}</Row>}
          </>
        )}
        {kind === "holiday" && (
          <>
            <Row label="Team member">{item.user_name}</Row>
            <Row label="From">{fmt(item.start_date, "EEE d MMM yyyy")}</Row>
            <Row label="To">{fmt(item.end_date, "EEE d MMM yyyy")}</Row>
            {item.note && <Row label="Note">{item.note}</Row>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
